import React from "react";
import isFailedAsset from "../../lib/isFailedAsset";

/**
 * AssetSlot
 * ---------
 * Renders the generated creative inside the preview, with platform-correct
 * aspect-ratio enforcement so the asset locks to the same shape it would
 * occupy on the real platform surface.
 *
 * Aspect ratios per Vella_Preview_Mockup_Spec.md (sec. 3):
 *   - "1:1"  square        — IG Feed / FB Feed default, LI Feed default
 *   - "4:5"  portrait      — IG Feed portrait + FB Feed portrait
 *   - "9:16" full-screen   — IG Reel / IG Story / TT Feed / Shorts
 *   - "16:9" landscape     — YouTube / X / LinkedIn video
 *
 * Aspect ratio is locked via CSS `aspectRatio`; if a video would be cropped
 * by the lock, the asset is `object-fit: cover` to keep parity with how the
 * platforms display it.
 *
 * Props:
 *   - asset:     {
 *                  type:           "image" | "video",
 *                  url:            string,
 *                  poster?:        string,                            // optional video poster
 *                  aspect_ratio:   "1:1" | "4:5" | "9:16" | "16:9",
 *                  upscale_model?: string,                            // reserved for Kling Omni 3.0
 *                }
 *   - className: optional class name.
 *   - style:     optional inline style overrides (merged last).
 *
 * Notes:
 *   - For `type === 'image'` we render an <img>.
 *   - For `type === 'video'` we render <video autoplay muted loop playsInline>.
 *   - For full-screen variants (9:16) the slot stretches to fill the device
 *     screen so the chrome's action rail can float on top.
 */
export default function AssetSlot({ asset, className, style }) {
  const { type, url, poster, aspect_ratio, upscale_model } = asset || {};

  // Map the spec ratios to CSS-friendly forms.
  const aspectMap = {
    "1:1": "1 / 1",
    "4:5": "4 / 5",
    "9:16": "9 / 16",
    "16:9": "16 / 9",
  };
  const aspectCss = aspect_ratio && aspectMap[aspect_ratio];

  // 9:16 is the full-screen lane (Reels / TikTok / Stories); the slot fills
  // the device, and the chrome overlays sit on top. Other ratios anchor at
  // the chrome's content area and flex-shrink to the locked shape.
  const isFullScreen = aspect_ratio === "9:16";

  const baseStyle = isFullScreen
    ? {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "#000",
        overflow: "hidden",
      }
    : {
        position: "relative",
        width: "100%",
        background: "#F1F5F9",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Aspect-ratio lock keeps the asset platform-accurate.
        aspectRatio: aspectCss || "1 / 1",
      };

  const commonProps = {
    className: ["vella-asset-slot", className].filter(Boolean).join(" "),
    "data-asset-type": type || "image",
    "data-aspect-ratio": aspect_ratio || undefined,
    "data-upscale-model": upscale_model || undefined,
    style: { ...baseStyle, ...(style || {}) },
  };

  const mediaStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  };

  // A failed / placeholder asset must never render as a real creative —
  // surface a quiet error state instead (defense-in-depth; the backend now
  // also stops emitting placehold.co stand-ins).
  if (isFailedAsset(url, asset?.error)) {
    return (
      <div {...commonProps}>
        <div style={emptyMessageStyle}>Couldn't generate this creative.</div>
      </div>
    );
  }

  if (type === "image") {
    return (
      <div {...commonProps}>
        {url ? (
          <img src={url} alt="" style={mediaStyle} />
        ) : (
          <div style={emptyMessageStyle}>No asset URL</div>
        )}
      </div>
    );
  }

  if (type === "video") {
    return (
      <div {...commonProps}>
        {url ? (
          <video
            src={url}
            poster={poster || undefined}
            autoPlay
            muted
            loop
            playsInline
            style={mediaStyle}
          />
        ) : (
          <div style={emptyMessageStyle}>No video URL</div>
        )}
        <UpscaleTo4KButton asset={asset} />
      </div>
    );
  }

  return (
    <div {...commonProps}>
      <div style={emptyMessageStyle}>
        Unsupported asset type: {String(type)}
      </div>
    </div>
  );
}

const emptyMessageStyle = {
  fontSize: 11,
  color: "#64748B",
  padding: 20,
  textAlign: "center",
};

/**
 * UpscaleTo4KButton — triggers Kling Omni 3.0 upscale for a video asset.
 *
 * Visibility rule:
 *   Rendered only when asset.type === 'video' AND asset.upscale_model !== 'kling_omni_3'.
 *   Hidden for images and for videos that have already been upscaled to kling_omni_3.
 */
export function UpscaleTo4KButton({ asset }) {
  if (!asset || asset.type !== "video") return null;
  if (asset.upscale_model === "kling_omni_3") return null;

  // The Kling Omni 3 upscale path isn't shipped yet, so the click
  // handler is a stub. Until the backend MCP tool is wired up we render
  // the button as "Coming soon" rather than letting the user click into
  // a silent no-op.
  return (
    <span
      data-testid="upscale-4k-coming-soon"
      aria-label="4K upscale coming soon"
      title="4K upscale is coming soon."
      style={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 2,
        padding: "5px 10px",
        fontSize: 11,
        fontWeight: 600,
        color: "rgba(255,255,255,0.85)",
        background: "rgba(0, 0, 0, 0.55)",
        border: "1px solid rgba(255, 255, 255, 0.22)",
        borderRadius: 8,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        userSelect: "none",
        letterSpacing: "0.02em",
      }}
    >
      4K · Coming soon
    </span>
  );
}
